import { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPinned, User as UserIcon } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { haptics } from "@/lib/haptics";

/**
 * Global bottom tab bar for top-level destinations. Only two today
 * (Trips, Profile) but the array is data-driven so a future third
 * destination costs one line.
 *
 * Where it renders:
 *   - `/dashboard`, `/profile`, `/trip/create`, and the auth surface.
 *
 * Where it hides:
 *   - Trip-detail routes (`/trip/:id/…`) — those screens already stack
 *     the TripActionBar; a third bar would overflow.
 *   - `/invite/:token` and any other public entry surface.
 *
 * The mount rule lives in this file (see `shouldShowBottomTabs`) so
 * consumers just call the component unconditionally from main.tsx.
 */

const TABS: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: "/dashboard", label: "Trips", icon: MapPinned },
  { to: "/profile", label: "Profile", icon: UserIcon },
];

export function shouldShowBottomTabs(pathname: string): boolean {
  // Show on the top-level destinations only.
  if (pathname === "/dashboard" || pathname === "/profile") return true;
  if (pathname === "/trip/create") return true;
  return false;
}

export function BottomTabs() {
  const location = useLocation();
  const visible = shouldShowBottomTabs(location.pathname);

  // Publish the bar's height as a CSS var so consumers (FAB, ad banner,
  // route bottom-padding) can stack cleanly above it without hard-coding
  // pixel values. Reset to 0 when the bar is hidden.
  //
  // Includes var(--safe-bottom) — the bar's own `paddingBottom`
  // below grows by that inset on devices with a home indicator / gesture
  // bar, so a consumer using only the 56px content height would undercount
  // the bar's real footprint and overlap it (e.g. the dashboard FAB).
  useEffect(() => {
    const html = document.documentElement;
    if (visible) {
      html.style.setProperty("--bottom-tabs-h", "calc(56px + var(--safe-bottom))");
    } else {
      html.style.removeProperty("--bottom-tabs-h");
    }
    return () => {
      html.style.removeProperty("--bottom-tabs-h");
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <nav
      aria-label="Primary"
      data-ad-clip="bottom"
      className={cn(
        "fixed inset-x-0 bottom-0 z-30",
        "border-t border-line bg-[color-mix(in_oklab,var(--bg-elevated)_92%,transparent)] backdrop-blur-md",
      )}
      style={{ paddingBottom: "var(--safe-bottom)" }}
    >
      <ul className="mx-auto flex max-w-md items-stretch justify-around px-2 pt-1.5 pb-2">
        {TABS.map((tab) => (
          <li key={tab.to} className="flex-1">
            <NavLink
              to={tab.to}
              onPointerDown={() => haptics.tap()}
              className={({ isActive }) =>
                cn(
                  "relative flex flex-col items-center gap-1 py-1.5 text-[11px] font-semibold transition-colors",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded-md",
                  isActive ? "text-primary" : "text-ink-subtle hover:text-ink",
                )
              }
              end
            >
              {({ isActive }) => (
                <>
                  <tab.icon className="h-5 w-5" strokeWidth={isActive ? 2.25 : 1.75} />
                  <span>{tab.label}</span>
                  {isActive ? (
                    <motion.span
                      layoutId="bottom-tab-active"
                      aria-hidden
                      className="absolute -top-1 left-1/2 h-1 w-8 -translate-x-1/2 rounded-full bg-primary"
                      transition={{ type: "spring", stiffness: 500, damping: 40 }}
                    />
                  ) : null}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * CSS spacer classname — apply to the bottom of a top-level route so
 * content doesn't sit behind the fixed tab bar. 64 px covers the bar
 * (56 px + 8 px breathing) plus the safe-area inset.
 */
export const BOTTOM_TABS_SPACER =
  "pb-[calc(64px+var(--safe-bottom))]";
